import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import DropdownMenu from "../../../components/DropdownMenu";
import Icon from "../../../components/Icon/Icon";
import { IconType } from "../../../enums/Icon";
import injectReducer from "../../../reduxFolder/injectReducer";
import warehouseReducer from "../../../reduxFolder/reducers/warehouse";
import { authSelectCanUseContainers } from "../../../reduxFolder/selectors/globalSelectors";
import { warehouseSelectBranches } from "../../../reduxFolder/selectors/warehouseSelectors";
import { WarehouseBranchMap } from "../../../types/api/warehouse";
import { MobileMenuButton } from "../../HeaderContainer/HeaderContainer.styles";
import {
  BurgerMenuLink,
  BurgerWrapper,
  ParcelsLink,
  Wrapper,
} from "../Parcels.styles";
import { buildParcelsNav } from "../parcelsNav";

type ParcelHeaderProps = {
  warehouseBranches: List<WarehouseBranchMap>;
  canUseContainers: boolean;
};

const ParcelHeader = ({
  warehouseBranches,
  canUseContainers,
}: ParcelHeaderProps) => {
  const { t, i18n } = useTranslation("common");
  const navRef = useRef<HTMLDivElement>(null);
  // Number of leading links that fit inline; null = all of them fit.
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const updateVisibleCount = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const navRect = nav.getBoundingClientRect();
    // Wrapper is display:none on small screens (the mobile header has its
    // own menu) — treat as "all fit" so the burger hides as well.
    if (navRect.width === 0) {
      setVisibleCount(null);
      return;
    }
    const children = Array.from(nav.children);
    let count = 0;
    for (const child of children) {
      // Folded links keep their layout slot (visibility: hidden), so
      // positions stay measurable no matter what is currently folded.
      if (
        child.getBoundingClientRect().right - navRect.left >
        navRect.width + 0.5
      ) {
        break;
      }
      count += 1;
    }
    setVisibleCount(count >= children.length ? null : count);
  }, []);

  const links = buildParcelsNav(warehouseBranches, t, { canUseContainers });

  // Re-measure before paint whenever the set of links or their labels change.
  useLayoutEffect(() => {
    updateVisibleCount();
  }, [updateVisibleCount, warehouseBranches, canUseContainers, i18n.language]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(nav);
    // Links too: label width changes (font load, language) reflow the row
    // without resizing it.
    Array.from(nav.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [updateVisibleCount, links.length]);

  const foldedLinks = visibleCount === null ? [] : links.slice(visibleCount);

  return (
    <>
      <Wrapper ref={navRef}>
        {links.map(({ to, label, end }, index) => (
          <ParcelsLink
            key={to}
            title={label}
            to={to}
            end={end}
            $folded={visibleCount !== null && index >= visibleCount}
          >
            {label}
          </ParcelsLink>
        ))}
      </Wrapper>
      {foldedLinks.length > 0 && (
        <BurgerWrapper>
          <DropdownMenu
            trigger={
              <MobileMenuButton
                aria-label={t("common.openMenu")}
                data-testid="open-parcels-menu"
              >
                <Icon type={IconType.MenuBurger} />
              </MobileMenuButton>
            }
          >
            {foldedLinks.map(({ to, label, end }) => (
              <BurgerMenuLink key={to} title={label} to={to} end={end}>
                {label}
              </BurgerMenuLink>
            ))}
          </DropdownMenu>
        </BurgerWrapper>
      )}
    </>
  );
};

const withReducer = injectReducer({
  key: "warehouse",
  reducer: warehouseReducer,
});

const withConnect = connect(
  createStructuredSelector({
    warehouseBranches: warehouseSelectBranches,
    canUseContainers: authSelectCanUseContainers,
  }),
  {},
);

const enhance = compose(withReducer, withConnect);

export default enhance(ParcelHeader);
